import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../../state'
import { Store } from '../store'

/**
 * Renders a single conversation item with badge, edit and delete actions.
 */
@ngCore.Component({
  imports: [
    ds.tag.Component,
    ds.overlay.centered.Component,
    ds.overlay.confirmation.Component,
    ds.layout.widget.Component,
    ds.input.text.Component,
    ds.input.select.single.Component,
    ds.input.select.multiple.Component,
    ds.button.Component,
  ],
  selector: 'atn-conversation-item',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see state.assistant.conversational.Group */
  public readonly assistantGroup = ngCore.inject(
    state.assistant.conversational.Group,
  )
  /**
   * The conversation to display
   */
  public readonly conversationSg =
    ngCore.input.required<ents.core.agent.Conversation>({
      alias: 'conversation',
    })

  /**
   * The name of the agent associated with the conversation
   */
  public readonly agentName = ngCore.computed(
    (agentId = this.conversationSg().agentId) =>
      this.assistantGroup.getAssistantDisplayByAgentId(agentId).name,
  )

  /**
   * @see {@link Store}
   */
  public readonly store = ngCore.inject(Store)

  /**
   * Whether the current user is the owner of the conversation. Shared
   * conversations are read-only for non-owners.
   */
  public readonly isOwner = ngCore.computed(
    () => this.conversationSg().ownerId === this.store.currentUserId(),
  )

  /**
   * Whether the conversation is shared with other users.
   */
  public readonly isShared = ngCore.computed(
    () => this.conversationSg().sharedWith.length > 0,
  )

  /**
   * A list of workspace items for the select component
   */
  public workspaceItems = ngCore.computed<
    readonly ds.input.select.models.Item<ents.core.agent.Workspace.Id>[]
  >(() =>
    this.assistantGroup.workspace.plugins.currentListPlugin.feature.selectors.entities
      .asSignal()
      .map(workspace => ({
        label: workspace.name,
        value: workspace.id,
      })),
  )

  /** @see {@link ds.color.Legacy} */
  public readonly sharedTagStyle = ds.color.Legacy.PredefinedStyle

  /**
   * Selects the conversation
   */
  public selectConversation(): void {
    this.assistantGroup.conversation.plugins.currentEntityPlugin.storage.actions.putInIdLookup
      .from('Dolomite.ChatPage.Conversation.Selection.Changed')
      .dispatch({
        value: this.conversationSg().id,
      })
  }

  /**
   * Edit a conversation name
   */
  public editConversation(event: Event): void {
    event.stopPropagation()
    this.assistantGroup.conversation.plugins.editingPlugin.feature.actions.edit
      .from('Dolomite.ChatPage.Conversation.Edit.Clicked')
      .dispatch({ id: this.conversationSg().id, makeCurrent: false })
  }

  /**
   * Mark a conversation for deletion (shows confirmation dialog)
   */
  public deleteConversation(event: Event): void {
    event.stopPropagation()
    this.assistantGroup.conversation.plugins.editingPlugin.feature.actions.markForDeletion
      .from('Dolomite.ChatPage.deleteConversation')
      .dispatch({
        id: this.conversationSg().id,
      })
  }

  /**
   * Confirm deletion of the marked conversation
   */
  public confirmDeleteConversation(): void {
    this.assistantGroup.conversation.plugins.editingPlugin.feature.actions.submit
      .from('Confirmation.Footer.Buttons.Confirm.Clicked')
      .dispatch({ delete: true })
  }

  /**
   * Cancel deletion of the marked conversation
   */
  public cancelDeleteConversation(): void {
    this.assistantGroup.conversation.plugins.editingPlugin.feature.actions.discard
      .from('Confirmation.Footer.Buttons.Cancel.Clicked')
      .dispatch({ delete: true, skipUnset: true })
  }

  /**
   * Update conversation
   */
  public updateConversation(
    updates: Partial<state.assistant.conversational.conversation.CreateEntity>,
  ): void {
    this.assistantGroup.conversation.plugins.editingPlugin.feature.actions.edit
      .from('Dolomite.ChatPage.Conversation.Updated')
      .dispatch({
        edit: { input: updates },
        id: this.conversationSg().id,
        makeCurrent: false,
      })
  }

  /**
   * Save conversation changes
   */
  public saveConversation(): void {
    this.assistantGroup.conversation.plugins.editingPlugin.feature.actions.submit
      .from('Dolomite.ChatPage.Conversation.SaveButton.Clicked')
      .dispatch({
        skipUnset: true,
        update: true,
      })
  }

  /**
   * Cancel conversation edit
   */
  public cancelEditConversation(): void {
    this.assistantGroup.conversation.plugins.editingPlugin.feature.actions.discard
      .from('Dolomite.ChatPage.Conversation.CancelButton.Clicked')
      .dispatch({
        skipUnset: true,
        update: true,
      })
  }
}
